import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WorkCalendarToken, WorkDayToken, ExceptionDayToken } from '@sleed/bizcommerce-api';
import { availableDatesApi } from '~/lib/api/client';
import {
	calendarToToken,
	workDayToToken,
	exceptionDayToToken,
	type WorkCalendarFormValues,
	type WorkDayFormValues,
	type ExceptionDayFormValues
} from '~/lib/schemas/available-date';

export const availableDateKeys = {
	all: ['available-dates'] as const,
	calendars: () => [...availableDateKeys.all, 'calendars'] as const,
	workDays: (wcId: number) => [...availableDateKeys.all, 'work-days', wcId] as const,
	exceptionDays: (wcId: number) => [...availableDateKeys.all, 'exception-days', wcId] as const
};

// ── Work calendars (master) ───────────────────────────────────────────────────
export function useWorkCalendars() {
	return useQuery<WorkCalendarToken[]>({
		queryKey: availableDateKeys.calendars(),
		queryFn: async () => (await availableDatesApi.getWorkCalendars()).data.items ?? []
	});
}

export function useWorkCalendarMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: availableDateKeys.calendars() });
	const create = useMutation({
		mutationFn: async (f: WorkCalendarFormValues) => (await availableDatesApi.insertWorkCalendar(calendarToToken(f))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (f: WorkCalendarFormValues) => (await availableDatesApi.updateWorkCalendar(calendarToToken(f))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await availableDatesApi.deleteWorkCalendar(id)).data,
		onSuccess: invalidate
	});
	return { create, update, remove };
}

// ── Work days (detail) ────────────────────────────────────────────────────────
export function useWorkDays(wcId: number | null) {
	return useQuery<WorkDayToken[]>({
		queryKey: availableDateKeys.workDays(wcId ?? 0),
		queryFn: async () => (await availableDatesApi.getWorkDaysById(wcId ?? 0)).data ?? [],
		enabled: wcId != null
	});
}

export function useWorkDayMutations(wcId: number) {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: availableDateKeys.workDays(wcId) });
	const create = useMutation({
		mutationFn: async (f: WorkDayFormValues) => (await availableDatesApi.insertWorkDays(workDayToToken(f))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (f: WorkDayFormValues) => (await availableDatesApi.updateWorkDays(workDayToToken(f))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await availableDatesApi.deleteWorkDay(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}

// ── Exception days (detail) ─────────────────────────────────────────────────
export function useExceptionDays(wcId: number | null) {
	return useQuery<ExceptionDayToken[]>({
		queryKey: availableDateKeys.exceptionDays(wcId ?? 0),
		queryFn: async () => (await availableDatesApi.getExceptionDaysById(wcId ?? 0)).data ?? [],
		enabled: wcId != null
	});
}

export function useExceptionDayMutations(wcId: number) {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: availableDateKeys.exceptionDays(wcId) });
	const create = useMutation({
		mutationFn: async (f: ExceptionDayFormValues) => (await availableDatesApi.insertExceptionDays(exceptionDayToToken(f))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (f: ExceptionDayFormValues) => (await availableDatesApi.updateExceptionDays(exceptionDayToToken(f))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await availableDatesApi.deleteExceptionDay(id)).data,
		onSuccess: invalidate
	});
	return { create, update, remove };
}
